import { Package } from "lucide-react";
import { DetailRow } from "@/components/shared/finance/DetailRow";
import { MonoAmount } from "@/components/shared/finance/MonoAmount";
import { MonoId } from "@/components/shared/finance/MonoId";
import { createPeekContent, LinkedRow } from "@/components/shared/relationships/peek-content";
import { registerPeekType } from "@/components/shared/relationships/peek-registry";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useInventoryProduct } from "@/features/inventory/hooks/use-inventory-data";
import { ABSENT_VALUE, formatCurrency } from "@/lib/format";
import { getProductDisplayName, getProductSubtitle } from "@/lib/product-display";

function ProductContent({
  product,
}: {
  product: NonNullable<ReturnType<typeof useInventoryProduct>["data"]>;
}) {
  return (
    <>
      <div className="space-y-3">
        <div className="flex items-start gap-3">
          {product.photo_url ? (
            <img
              alt=""
              className="size-12 rounded-xl object-cover"
              height={48}
              loading="lazy"
              src={product.photo_url}
              width={48}
            />
          ) : (
            <div className="flex size-12 items-center justify-center rounded-xl bg-muted text-muted-foreground">
              <Package className="size-5" />
            </div>
          )}
          <div className="min-w-0 space-y-1">
            <div className="font-semibold text-lg">
              {getProductDisplayName(product, ABSENT_VALUE)}
            </div>
            {getProductSubtitle(product) ? (
              <div className="text-muted-foreground text-sm">{getProductSubtitle(product)}</div>
            ) : null}
            {product.uid ? <MonoId className="text-sm">{product.uid}</MonoId> : null}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {product.category ? <Badge variant="secondary">{product.category}</Badge> : null}
          {product.kanban_status ? (
            <Badge variant="outline">{product.kanban_status.replaceAll("_", " ")}</Badge>
          ) : null}
        </div>
      </div>
      <Separator />
      <div className="space-y-2">
        <DetailRow label="SKU" value={product.sku || ABSENT_VALUE} />
        <DetailRow label="Quantity" value={product.quantity ?? 0} />
        {/*
         * The three derived positions, not a sum: a sampled unit has left the
         * building without being bought, so it is neither in stock nor sold.
         * Without these, the quick view answered "how many are on the shelf"
         * with how many arrived.
         */}
        <DetailRow label="In stock" value={product.qty_in_stock} />
        <DetailRow label="On sample" value={product.qty_sampled} />
        <DetailRow label="Sold" value={product.qty_sold} />
        <DetailRow label="Cost" value={<MonoAmount>{formatCurrency(product.cost)}</MonoAmount>} />
        <DetailRow
          label="Total"
          value={<MonoAmount>{formatCurrency(product.total_cost)}</MonoAmount>}
        />
        <LinkedRow
          id={product.purchase_order_id}
          label="Purchase Order"
          name={product.purchase_order_uid}
          type="purchase-order"
        />
      </div>
    </>
  );
}

/** Product quick-view content, registered on import via `src/app/peek-registrations.ts`. */
export const ProductPeekContent = createPeekContent({
  emptyMessage: "No product found",
  render: (product) => <ProductContent product={product} />,
  useDetail: useInventoryProduct,
});

registerPeekType("product", ProductPeekContent);
